// particles.ts — one pooled THREE.Points system per blend mode. Every effect
// in the game (skin ambience, trails, eat bursts, kill shockwaves) goes
// through here, so a whole round renders particles in two draw calls.
import * as THREE from "three";

export const Shape = { Dot: 0, Spark: 1, Heart: 2, Ring: 3 } as const;
export type Shape = (typeof Shape)[keyof typeof Shape];

export interface EmitOptions {
  x: number;
  y: number;
  z: number;
  vx?: number;
  vy?: number;
  vz?: number;
  color: THREE.ColorRepresentation;
  size: number;
  /** Size multiplier at the end of life (default 0). */
  sizeEnd?: number;
  life: number;
  gravity?: number;
  /** Velocity damping per second, 0..1-ish (default 0). */
  drag?: number;
  shape?: Shape;
  alpha?: number;
  /** Rotation speed for sparks/hearts, radians per second. */
  spin?: number;
}

const VERT = `
attribute vec3 color;
attribute float size;
attribute float alpha;
attribute float shape;
attribute float angle;
varying vec3 vColor;
varying float vAlpha;
varying float vShape;
varying float vAngle;
uniform float uScale;
void main() {
  vColor = color;
  vAlpha = alpha;
  vShape = shape;
  vAngle = angle;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = size * uScale / max(0.1, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = `
varying vec3 vColor;
varying float vAlpha;
varying float vShape;
varying float vAngle;
void main() {
  vec2 p = gl_PointCoord * 2.0 - 1.0;
  float c = cos(vAngle), s = sin(vAngle);
  p = mat2(c, -s, s, c) * p;
  float a;
  if (vShape < 0.5) {
    a = smoothstep(1.0, 0.15, length(p));
  } else if (vShape < 1.5) {
    // four-point sparkle
    vec2 q = abs(p);
    float star = max(0.0, 1.0 - (q.x * 6.0 + q.y * 1.0)) + max(0.0, 1.0 - (q.y * 6.0 + q.x * 1.0));
    a = clamp(star + smoothstep(0.45, 0.0, length(p)), 0.0, 1.0);
  } else if (vShape < 2.5) {
    // heart
    vec2 h = vec2(p.x, -p.y * 1.1 + 0.25);
    float r = h.x * h.x + h.y * h.y - 0.35;
    a = 1.0 - smoothstep(-0.02, 0.06, r * r * r - h.x * h.x * h.y * h.y * h.y);
  } else {
    float d = length(p);
    a = smoothstep(0.12, 0.0, abs(d - 0.8));
  }
  if (a * vAlpha < 0.01) discard;
  gl_FragColor = vec4(vColor, a * vAlpha);
}`;

export class ParticleSystem {
  readonly points: THREE.Points;
  private readonly capacity: number;
  private readonly position: Float32Array;
  private readonly color: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;
  private readonly shape: Float32Array;
  private readonly angle: Float32Array;
  private readonly velocity: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly baseSize: Float32Array;
  private readonly sizeEnd: Float32Array;
  private readonly baseAlpha: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly spin: Float32Array;
  private cursor = 0;
  private readonly tmpColor = new THREE.Color();
  private readonly material: THREE.ShaderMaterial;

  constructor(capacity: number, additive: boolean) {
    this.capacity = capacity;
    this.position = new Float32Array(capacity * 3);
    this.color = new Float32Array(capacity * 3);
    this.size = new Float32Array(capacity);
    this.alpha = new Float32Array(capacity);
    this.shape = new Float32Array(capacity);
    this.angle = new Float32Array(capacity);
    this.velocity = new Float32Array(capacity * 3);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.baseSize = new Float32Array(capacity);
    this.sizeEnd = new Float32Array(capacity);
    this.baseAlpha = new Float32Array(capacity);
    this.gravity = new Float32Array(capacity);
    this.drag = new Float32Array(capacity);
    this.spin = new Float32Array(capacity);

    const geometry = new THREE.BufferGeometry();
    const attr = (array: Float32Array, size: number) => new THREE.BufferAttribute(array, size).setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute("position", attr(this.position, 3));
    geometry.setAttribute("color", attr(this.color, 3));
    geometry.setAttribute("size", attr(this.size, 1));
    geometry.setAttribute("alpha", attr(this.alpha, 1));
    geometry.setAttribute("shape", attr(this.shape, 1));
    geometry.setAttribute("angle", attr(this.angle, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uScale: { value: 400 } },
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.points = new THREE.Points(geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = additive ? 6 : 5;
  }

  /** Pixel scale for point sizes; call on resize with the canvas height. */
  setViewportHeight(pixels: number, fovDeg: number) {
    this.material.uniforms.uScale!.value = pixels / (2 * Math.tan((fovDeg * Math.PI) / 360));
  }

  emit(o: EmitOptions) {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;
    this.position[i * 3] = o.x;
    this.position[i * 3 + 1] = o.y;
    this.position[i * 3 + 2] = o.z;
    this.velocity[i * 3] = o.vx ?? 0;
    this.velocity[i * 3 + 1] = o.vy ?? 0;
    this.velocity[i * 3 + 2] = o.vz ?? 0;
    this.tmpColor.set(o.color);
    this.color[i * 3] = this.tmpColor.r;
    this.color[i * 3 + 1] = this.tmpColor.g;
    this.color[i * 3 + 2] = this.tmpColor.b;
    this.baseSize[i] = o.size;
    this.sizeEnd[i] = o.sizeEnd ?? 0;
    this.size[i] = o.size;
    this.baseAlpha[i] = o.alpha ?? 1;
    this.alpha[i] = this.baseAlpha[i]!;
    this.shape[i] = o.shape ?? Shape.Dot;
    this.angle[i] = Math.random() * Math.PI * 2;
    this.spin[i] = o.spin ?? 0;
    this.life[i] = o.life;
    this.maxLife[i] = o.life;
    this.gravity[i] = o.gravity ?? 0;
    this.drag[i] = o.drag ?? 0;
  }

  update(dt: number) {
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i]! <= 0) {
        if (this.alpha[i] !== 0) { this.alpha[i] = 0; this.size[i] = 0; }
        continue;
      }
      this.life[i] = this.life[i]! - dt;
      const t = 1 - Math.max(0, this.life[i]!) / this.maxLife[i]!;
      const damp = Math.max(0, 1 - this.drag[i]! * dt);
      const j = i * 3;
      this.velocity[j] = this.velocity[j]! * damp;
      this.velocity[j + 1] = this.velocity[j + 1]! * damp - this.gravity[i]! * dt;
      this.velocity[j + 2] = this.velocity[j + 2]! * damp;
      this.position[j] = this.position[j]! + this.velocity[j]! * dt;
      this.position[j + 1] = this.position[j + 1]! + this.velocity[j + 1]! * dt;
      this.position[j + 2] = this.position[j + 2]! + this.velocity[j + 2]! * dt;
      this.size[i] = this.baseSize[i]! * (1 + (this.sizeEnd[i]! - 1) * t);
      // quick fade-in, smooth fade-out
      this.alpha[i] = this.baseAlpha[i]! * Math.min(1, t * 8) * (1 - t * t);
      this.angle[i] = this.angle[i]! + this.spin[i]! * dt;
    }
    const geometry = this.points.geometry;
    for (const name of ["position", "size", "alpha", "color", "shape", "angle"]) {
      (geometry.getAttribute(name) as THREE.BufferAttribute).needsUpdate = true;
    }
  }

  dispose() {
    this.points.geometry.dispose();
    this.material.dispose();
  }
}

/** Additive (glow) + normal (debris) pair used by the engine and previews. */
export class Effects {
  readonly glow = new ParticleSystem(1600, true);
  readonly solid = new ParticleSystem(900, false);
  readonly group = new THREE.Group();

  constructor() {
    this.group.add(this.solid.points, this.glow.points);
  }

  setViewportHeight(pixels: number, fovDeg: number) {
    this.glow.setViewportHeight(pixels, fovDeg);
    this.solid.setViewportHeight(pixels, fovDeg);
  }

  update(dt: number) {
    this.glow.update(dt);
    this.solid.update(dt);
  }

  dispose() {
    this.glow.dispose();
    this.solid.dispose();
  }
}
